import { useClipboard } from "@mantine/hooks";
import { useI18n } from "@wojo/localization";
import { IconButton } from "@wojo/ui";
import React from "react";
import { FaFacebookF, FaPinterest, FaRegEnvelope } from "react-icons/fa";
import { FiCheck, FiLink } from "react-icons/fi";
import { RiTwitterXFill } from "react-icons/ri";
import {
    EmailShareButton,
    FacebookShareButton,
    PinterestShareButton,
    TwitterShareButton,
} from "react-share";
import styles from "./SocialMediaShare.module.scss";

export type SocialMediaShareProps = {
    url?: string;
    cardImage?: string;
    pinterestDescription?: string;
    twitterTitle?: string;
    emailBody?: string;
    emailSubject?: string;
    hideFacebook?: boolean;
    hideCopy?: boolean;
    onSocialMediaShareClick?: (platform: string) => void;
};

export const SocialMediaShare: React.FC<SocialMediaShareProps> = ({
    url,
    cardImage,
    pinterestDescription,
    twitterTitle,
    emailBody,
    emailSubject,
    hideFacebook,
    hideCopy,
    onSocialMediaShareClick,
}) => {
    const clipboard = useClipboard({ timeout: 2000 });
    const { global } = useI18n("global");
    const defaultUrl =
        typeof window !== "undefined" ? window.location.href : "";
    const pageUrl = url || defaultUrl;
    const showFacebook = !hideFacebook;
    const showPinterest = !!cardImage && !!pinterestDescription;
    const showTwitter = !!twitterTitle;
    const showEmail = !!emailBody && !!emailSubject;
    const showCopy = !hideCopy;

    const copyText = clipboard.copied
        ? global.socials.copy.copiedLabel.l()
        : global.socials.copy.label.l();

    const shareLabel = (social: string) =>
        global.socials.shareLabel.l({ platform: social });

    return (
        <div className={styles.socials}>
            {showFacebook && (
                <FacebookShareButton
                    url={pageUrl}
                    type="button"
                    className={styles.social}
                    aria-label={shareLabel(global.socials.facebook.label.l())}
                    onClick={() => {
                        onSocialMediaShareClick?.(
                            global.socials.facebook.label.l(),
                        );
                    }}
                >
                    <IconButton
                        variant="secondary"
                        palette="monochrome"
                        tag="div"
                        className={styles.icon}
                        icon={<FaFacebookF aria-hidden />}
                        ariaLabel=""
                        aria-hidden
                        title={shareLabel(global.socials.facebook.label.l())}
                    />
                </FacebookShareButton>
            )}
            {showPinterest && (
                <PinterestShareButton
                    url={pageUrl}
                    type="button"
                    description={pinterestDescription}
                    media={cardImage}
                    windowWidth={1000}
                    windowHeight={730}
                    className={styles.social}
                    aria-label={shareLabel(global.socials.pinterest.label.l())}
                    onClick={() => {
                        onSocialMediaShareClick?.(
                            global.socials.pinterest.label.l(),
                        );
                    }}
                >
                    <IconButton
                        variant="secondary"
                        palette="monochrome"
                        tag="div"
                        className={styles.icon}
                        icon={<FaPinterest aria-hidden />}
                        ariaLabel=""
                        aria-hidden
                        title={shareLabel(global.socials.pinterest.label.l())}
                    />
                </PinterestShareButton>
            )}
            {showTwitter && (
                <TwitterShareButton
                    url={pageUrl}
                    type="button"
                    title={twitterTitle}
                    className={styles.social}
                    aria-label={shareLabel(global.socials.twitter.label.l())}
                    onClick={() => {
                        onSocialMediaShareClick?.(
                            global.socials.twitter.label.l(),
                        );
                    }}
                >
                    <IconButton
                        variant="secondary"
                        palette="monochrome"
                        tag="div"
                        className={styles.icon}
                        icon={<RiTwitterXFill aria-hidden />}
                        ariaLabel=""
                        aria-hidden
                        title={shareLabel(global.socials.twitter.label.l())}
                    />
                </TwitterShareButton>
            )}
            {showEmail && (
                <EmailShareButton
                    openShareDialogOnClick
                    url={pageUrl}
                    type="button"
                    subject={emailSubject}
                    body={emailBody}
                    className={styles.social}
                    aria-label={global.socials.email.label.l()}
                    onClick={() => {
                        onSocialMediaShareClick?.(
                            global.socials.email.label.l(),
                        );
                    }}
                >
                    <IconButton
                        variant="secondary"
                        palette="monochrome"
                        tag="div"
                        className={styles.icon}
                        icon={<FaRegEnvelope aria-hidden />}
                        ariaLabel=""
                        aria-hidden
                        title={global.socials.email.label.l()}
                    />
                </EmailShareButton>
            )}
            {showCopy && (
                <IconButton
                    type="button"
                    variant="secondary"
                    palette="monochrome"
                    style={{ padding: 0 }}
                    icon={
                        clipboard.copied ? (
                            <FiCheck aria-hidden />
                        ) : (
                            <FiLink aria-hidden />
                        )
                    }
                    ariaLabel={copyText}
                    title={copyText}
                    onClick={() => {
                        {
                            onSocialMediaShareClick?.(
                                global.socials.copy.label.l(),
                            );
                            clipboard.copy(pageUrl);
                        }
                    }}
                />
            )}
        </div>
    );
};
